/**
 * wildfireIcons.ts
 * صور لهب واقعية للحرائق — تحلّ محلّ رمز 🔥 النصّي.
 *
 * لماذا لا الإيموجي: خطّه يختلف بين الأنظمة (Apple/Google/Windows يرسمونه بأشكال وألوان
 * مختلفة تماماً)، فلا سيطرة على تباينه ولا على شكله. وهذا صار مُلحّاً بعد أن أصبحت
 * الحرائق تُرسم فوق صور القمر الصناعي الساطعة لا فوق قاعدة داكنة موحّدة فقط.
 *
 * **الشكل من تصميم Figma الذي زوّد به المستخدم (public/icons/figma/fire-1.png)، واللون منّا.**
 * جاء الشكل بنسختين، سوداء وبيضاء، واستُبعدتا كلتاهما: الأسود يختفي فوق القاعدة الداكنة
 * وفوق محيطات صور الأقمار وليلها، والأبيض يختفي فوق الغيوم والصحارى — ويُقرأ على الخرائط
 * «لا بيانات» لا «نار». البرتقالي المحمرّ لا يتكرّر في أيّ قاعدة عندنا ويُقرأ ناراً بلا مفتاح.
 *
 * الجسم برتقالي (متوسّط) أو أحمر (كثيف)، واللهب الداخلي أصفر — الأسخن في المركز كالنار
 * الحقيقية. وكان في الأصل **ثقباً شفّافاً** لا بكسلات بيضاء، فحُدّد بأنه الشفّاف المحاط
 * بالجسم والمنفصل عن الخلفية، ثم مُلئ. وحولهما حدٌّ داكن دافئ **مخبوز في الصورة**: الظلال
 * بـCSS كانت أغلى ما في هذه الطبقة (راجع رأس WildfireLayer.tsx)، والحدّ هنا مجّاني.
 *
 * بارتفاع 128 بكسل: ضعف أكبر حجم عرض على الخريطة، فتبقى حادّة على شاشات DPR=2.
 * والاسم `flame-*` لا `fire-*`: أيقونات هذا المجلّد مخزّنة يوماً في المتصفّحات، فالاسم
 * القديم كان سيُبقي اللهب السابق عند بعض الزوّار حتى الغد.
 *
 * **صور خلفية، لا وسوم <img> ولا SVG داخل الصفحة** — نفس أسلوب BOLT_SVG في
 * LightningLayer.tsx ولنفس السبب: عنصر `div` واحد بلا أبناء لكل بؤرة. عند مئات البؤر
 * (موسم حرائق نشط) هذا يمنع مئات أشجار DOM، والمتصفّح ينقّط كل صورة مرّة واحدة ويعيد
 * استعمال النقطية في كل البؤر التي تشاركها.
 */

/**
 * لونٌ واحد: الأحمر، بطلب المستخدم (2026-09-21) — كرموز Zoom Earth، الحريق حريق.
 * البرتقالي للمتوسّط أُسقط؛ الشدّة ما زالت تحكم **الترتيب** (الأقوى يبقى عند التخفيف)
 * لا اللون. والاسم جديد (`flame-red`) لنفس سبب الكاش المذكور أعلاه.
 */
export const FLAME_TIERS = 1;

const BASE = `${import.meta.env.BASE_URL}icons/wildfire/`;

/** نسبة العرض إلى الارتفاع — مقيسة من الملفّ المنشور (102×128). */
const FLAME_ASPECT = 0.797;

export function flameUrl(_tier: number): string {
    return `url("${BASE}flame-red.png")`;
}

export function flameAspect(_tier: number): number {
    return FLAME_ASPECT;
}

/**
 * يحوّل شدّة عددية إلى درجة عرض (4 أو 5 فقط — راجع تعليق FLAME_TIERS أعلاه).
 *
 * المدخل من normalizeIntensity في wildfireService.ts وهو FRP (قدرة الإشعاع الحراري
 * بالميغاواط) مقسوماً على 500 ومقصوصاً في [0.15, 1]. عتبة واحدة تكفي الآن بدرجتين.
 */
export function intensityTier(intensity: number): number {
    return intensity >= 0.5 ? 5 : 4;
}
